// Global Instructions Rule Applied!
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Input, Popover, List, Typography, Spin } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faHandshake,
  faExpandArrowsAlt,
  faTimes,
  faUser,
  faBuilding
} from '@fortawesome/free-solid-svg-icons'
import { searchDealsForPopup } from '../../app_modules/deals/services/dealsService.ts'
import { searchContactsForPopup } from '../../app_modules/contacts/services/contactsService.ts'
import { searchCompaniesForPopup } from '../../app_modules/companies/services/companiesService.ts'
import { searchFundraisingForPopup } from '../../app_modules/fundraising/services/fundraisingService.ts'
import { searchFundsForPopup, searchCompaniesInFundForPopup } from '../../app_modules/funds/services/fundsService.ts'
import { getAccessToken } from '../auth/authService'
import { store } from '../../state_manager/store.ts'
import { portalColors } from '../../portal-configs/colors'
import { useLookups } from '../../app_modules/global_configs/hooks/useLookups.ts'
import { DealFields } from '../../state_manager/data_models/deal_record.ts'
import { ContactRecordFields } from '../../state_manager/data_models/contact_record.ts'
import { ContactAccountsModelFields } from '../../state_manager/data_models/contact_accounts_model.ts'

const { Text } = Typography

/**
 * SearchPopover Component
 *
 * A search input with popover that shows search results.
 * Supports both server-side search (for deals) and client-side search (for local data).
 *
 * @param {Object} props - Component props
 * @param {string} [props.placeholder] - Placeholder text for the search input
 * @param {(item: Record<string, any>) => void} [props.onItemClick] - Callback when a search result is clicked (receives the item)
 * @param {(item: Record<string, any>) => void} [props.onExpandClick] - Callback when the expand icon is clicked (receives the item); omit it and no expand icon renders
 * @param {string} [props.searchService] - Server-side search service: 'deals' | 'contacts' | 'accounts' | 'prospects' | 'funds' | 'fund-investment-summary'. Omit for client-side search over `data`
 * @param {Record<string, unknown>} [props.searchExtraContext] - Extra context for services that need more than a term, e.g. `{ fundId }` for 'fund-investment-summary'
 * @param {Array<Record<string, any>>} [props.data] - Local data array for client-side searching
 * @param {string[]} [props.searchKeys] - Keys to search in for client-side search
 * @param {((item: Record<string, any>) => string) | string} [props.displayKey] - Function or key giving the display text for client-side results
 * @param {string} [props.emptyMessage] - Message to show when no results are found
 * @param {number} [props.maxResults] - Maximum number of results to show (default: 50)
 * @param {string} [props.className] - Additional CSS classes
 */
export default function SearchPopover({
  placeholder = 'Search...',
  onItemClick,
  onExpandClick,
  searchService,
  searchExtraContext,
  data,
  searchKeys,
  displayKey,
  emptyMessage = 'No results found',
  maxResults = 50,
  className = ''
}) {
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [error, setError] = useState(null)

  const { getLookupLabel } = useLookups()

  // Determine if we're using client-side or server-side search
  const isClientSearch = useMemo(() => {
    return !!(data && searchKeys && Array.isArray(data))
  }, [data, searchKeys])

  // Client-side search function
  const performClientSearch = useCallback(
    (searchTerm, dataArray, keys) => {
      if (!searchTerm || searchTerm.trim().length < 1) {
        return []
      }

      const term = searchTerm.toLowerCase().trim()
      return dataArray
        .filter((item) => {
          return keys.some((key) => {
            const value = item[key]
            if (value == null) return false
            return String(value).toLowerCase().includes(term)
          })
        })
        .slice(0, maxResults)
    },
    [maxResults]
  )

  // Server-side search function
  const performServerSearch = useCallback(
    async (searchTerm) => {
      setLoading(true)
      setError(null)

      try {
        const currentToken = getAccessToken(store.getState().auth)
        let results = []

        if (searchService === 'deals') {
          const response = await searchDealsForPopup(searchTerm, maxResults, currentToken)
          if (response.error) {
            setError(response.error)
          } else {
            results = response.data
          }
        } else if (searchService === 'contacts') {
          const response = await searchContactsForPopup(searchTerm, maxResults, currentToken)
          if (response.error) {
            setError(response.error)
          } else {
            results = response.data
          }
        } else if (searchService === 'accounts') {
          const response = await searchCompaniesForPopup(searchTerm, maxResults, currentToken)
          if (response.error) {
            setError(response.error)
          } else {
            results = response.data
          }
        } else if (searchService === 'prospects') {
          const response = await searchFundraisingForPopup(searchTerm, maxResults, currentToken)
          if (response.error) {
            setError(response.error)
          } else {
            results = response.data
          }
        } else if (searchService === 'funds') {
          const response = await searchFundsForPopup(searchTerm, maxResults, currentToken)
          if (response.error) {
            setError(response.error)
          } else {
            results = response.data
          }
        } else if (searchService === 'fund-investment-summary') {
          const fundId = searchExtraContext?.fundId
          if (!fundId) {
            results = []
          } else {
            const response = await searchCompaniesInFundForPopup(fundId, searchTerm, maxResults, currentToken)
            if (response.error) {
              setError(response.error)
            } else {
              results = response.data
            }
          }
        }

        setSearchResults(results)
      } catch (err) {
        console.error('Search error:', err)
        setError('Search failed. Please try again.')
        setSearchResults([])
      } finally {
        setLoading(false)
      }
    },
    [searchService, maxResults]
  )

  // Main search function that handles both client and server search
  const performSearch = useCallback(
    async (searchTerm) => {
      if (!searchTerm || searchTerm.trim().length < (isClientSearch ? 1 : 2)) {
        setSearchResults([])
        setError(null)
        return
      }

      if (isClientSearch) {
        // Client-side search - immediate results
        const results = performClientSearch(searchTerm, data, searchKeys)
        setSearchResults(results)
        setError(null)
        setLoading(false)
      } else {
        // Server-side search - async with loading state
        await performServerSearch(searchTerm)
      }
    },
    [isClientSearch, performClientSearch, performServerSearch, data, searchKeys]
  )

  // Debounce search input
  useEffect(() => {
    const delay = isClientSearch ? 100 : 300 // Faster debounce for client search
    const delayDebounce = setTimeout(() => {
      performSearch(searchValue)
    }, delay)

    return () => clearTimeout(delayDebounce)
  }, [searchValue, performSearch, isClientSearch])

  // Handle item click
  const handleItemClick = (item) => {
    setOpen(false)
    // Don't clear search value and results - let user manually clear

    if (onItemClick) {
      onItemClick(item)
    }
  }

  // Handle expand click
  const handleExpandClick = (item, event) => {
    event.stopPropagation() // Prevent triggering the item click
    setOpen(false)
    // Don't clear search value and results - let user manually clear

    if (onExpandClick) {
      onExpandClick(item)
    }
  }

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const value = e.target.value
    setSearchValue(value)

    if (value.trim().length === 0) {
      setSearchResults([])
      setError(null) // Clear any errors when clearing input
      // Don't change open state here - let focus control it
    }
  }, [])

  // Format display text based on search type
  const formatItemDisplay = (item) => {
    if (isClientSearch) {
      // For client-side search, use the displayKey function or string
      let title = 'Unknown'
      if (typeof displayKey === 'function') {
        title = displayKey(item)
      } else if (typeof displayKey === 'string') {
        title = item[displayKey] || 'Unknown'
      } else {
        // Fallback: try common name fields
        title = item.name || item.title || item.display_name || 'Unknown'
      }

      return {
        title,
        company: '',
        pipelineStage: ''
      }
    } else if (searchService === 'deals') {
      // Server-side deals search
      return {
        title: item[DealFields.deals__name] || '- No Deal Name -',
        company: item[DealFields.accounts__name] || 'No Company',
        pipelineStage:
          item[DealFields.pipeline_deals__stage__label] ||
          getLookupLabel(item[DealFields.pipeline_deals__stage]) ||
          'No Stage'
      }
    } else if (searchService === 'contacts') {
      // Server-side contacts search
      const firstName = item[ContactRecordFields.contacts__first_name] || ''
      const lastName = item[ContactRecordFields.contacts__last_name] || ''
      const preferredName = item[ContactRecordFields.contacts__preferred_name] || ''
      const email = item[ContactRecordFields.contacts__email] || ''
      const currentRoles = item['prog__current_roles'] || []
      const currentEmployer =
        Array.isArray(currentRoles) && currentRoles.length > 0
          ? currentRoles[0]?.[ContactAccountsModelFields.account_name] || ''
          : ''

      return {
        title: `${firstName} ${lastName}`.trim() || preferredName || email || '- No Contact Name -',
        company: currentEmployer || '',
        pipelineStage: email || ''
      }
    } else if (searchService === 'accounts') {
      // Server-side companies search
      return {
        title: item.label || item.legal_name || '- No Company Name -',
        company: '',
        pipelineStage: item.legal_name || ''
      }
    } else if (searchService === 'prospects') {
      // Server-side prospects search
      const investorName = item.accounts__name || item.investor_name || ''
      const fundName = item.funds__name || item.fund_name || ''
      const pipelineStage = item.pipeline_prospects__stage__label || item.pipeline_stage__label || ''

      return {
        title: investorName || '- No Investor Name -',
        company: fundName || '',
        pipelineStage: pipelineStage || ''
      }
    } else if (searchService === 'funds') {
      return {
        title: item.funds__name || item.funds__legal_name || '- No Fund Name -',
        company: item.funds__legal_name || '',
        pipelineStage: item.funds__initial_closing_date_fmt || ''
      }
    } else if (searchService === 'fund-investment-summary') {
      return {
        title: item.accounts__name || '- No Company Name -',
        company: item.accounts__industry__label || '',
        pipelineStage: item.total_value_fmt || ''
      }
    }

    return {
      title: item.name || 'Unknown',
      company: '',
      pipelineStage: ''
    }
  }

  // Render search results content
  const renderContent = () => {
    if (loading) {
      const searchType = isClientSearch ? 'items' : searchService || 'deals'
      return (
        <div className='p-6 text-center'>
          <div className='flex flex-col items-center space-y-3'>
            <div className='relative'>
              <Spin size='default' />
            </div>
            <div>
              <Text
                className='font-medium'
                style={{
                  color: portalColors.neutral[700],
                  fontSize: '14px'
                }}
              >
                Searching {searchType}...
              </Text>
              <div className='mt-1'>
                <Text
                  style={{
                    color: portalColors.neutral[500],
                    fontSize: '12px'
                  }}
                >
                  Finding matches for "{searchValue}"
                </Text>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className='p-6 text-center'>
          <div className='flex flex-col items-center space-y-3'>
            <div
              className='w-12 h-12 rounded-full flex items-center justify-center'
              style={{ backgroundColor: '#fef2f2' }}
            >
              <FontAwesomeIcon
                icon={faTimes}
                style={{
                  color: '#ef4444',
                  fontSize: '18px'
                }}
              />
            </div>
            <div>
              <Text
                className='font-medium'
                style={{
                  color: '#dc2626',
                  fontSize: '14px'
                }}
              >
                Search failed
              </Text>
              <div className='mt-1'>
                <Text
                  style={{
                    color: portalColors.neutral[500],
                    fontSize: '12px'
                  }}
                >
                  {error}
                </Text>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // Only show "No results found" if user has searched but no results came back
    if (searchResults.length === 0 && searchValue.trim().length >= (isClientSearch ? 1 : 2)) {
      return (
        <div className='p-6 text-center'>
          <div className='flex flex-col items-center space-y-3'>
            <div
              className='w-12 h-12 rounded-full flex items-center justify-center'
              style={{ backgroundColor: portalColors.neutral[100] }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                style={{
                  color: portalColors.neutral[400],
                  fontSize: '18px'
                }}
              />
            </div>
            <div>
              <Text
                className='font-medium'
                style={{
                  color: portalColors.neutral[700],
                  fontSize: '14px'
                }}
              >
                {emptyMessage}
              </Text>
              <div className='mt-1'>
                <Text
                  style={{
                    color: portalColors.neutral[500],
                    fontSize: '12px'
                  }}
                >
                  No matches for "{searchValue}"
                </Text>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // Show placeholder when field is focused but no search text
    if (searchResults.length === 0 && searchValue.trim().length < (isClientSearch ? 1 : 2) && isFocused) {
      const searchHint = isClientSearch
        ? `Type to search ${searchKeys ? searchKeys.join(', ') : 'items'}`
        : `Type to find ${
            searchService === 'contacts'
              ? 'contacts by name or email'
              : searchService === 'accounts'
                ? 'companies by name, city, or website'
                : searchService === 'prospects'
                  ? 'prospects by investor or fund name'
                  : searchService === 'funds'
                    ? 'funds by name'
                    : searchService === 'fund-investment-summary'
                      ? 'companies in this fund by name'
                      : 'deals by name, company, or details'
          }`
      const searchTitle = isClientSearch
        ? 'Search items'
        : `Search ${
            searchService === 'contacts'
              ? 'contacts'
              : searchService === 'accounts'
                ? 'companies'
                : searchService === 'prospects'
                  ? 'prospects'
                  : searchService === 'funds'
                    ? 'funds'
                    : searchService === 'fund-investment-summary'
                      ? 'investments'
                      : 'deals'
          } table`

      return (
        <div className='p-6 text-center'>
          <div className='flex flex-col items-center space-y-3'>
            <div
              className='w-12 h-12 rounded-full flex items-center justify-center'
              style={{ backgroundColor: portalColors.primary[50] }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                style={{
                  color: portalColors.primary[400],
                  fontSize: '20px'
                }}
              />
            </div>
            <div>
              <Text
                className='font-medium'
                style={{
                  color: portalColors.neutral[700],
                  fontSize: '14px'
                }}
              >
                {searchTitle}
              </Text>
              <div className='mt-1'>
                <Text
                  style={{
                    color: portalColors.neutral[500],
                    fontSize: '12px'
                  }}
                >
                  {searchHint}
                </Text>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // Don't show anything when no search text and not focused
    if (searchResults.length === 0 && !isFocused) {
      return null
    }

    return (
      <div
        className='overflow-y-auto w-full'
        style={{
          height: '600px',
          maxHeight: '600px'
        }}
      >
        <div className='p-2'>
          <Text
            className='font-medium text-xs uppercase tracking-wide px-3 py-2 block'
            style={{
              color: portalColors.neutral[500],
              letterSpacing: '0.05em'
            }}
          >
            Search Results ({searchResults.length})
          </Text>
        </div>
        <List
          size='small'
          dataSource={searchResults}
          renderItem={(item) => {
            const display = formatItemDisplay(item)
            return (
              <List.Item
                className='cursor-pointer hover:bg-blue-50 mx-1 mb-1 rounded-lg border-0 group transition-all duration-200'
                onClick={() => handleItemClick(item)}
                style={{
                  padding: '5px 12px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  border: '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.border = `1px solid ${portalColors.primary[200]}`
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '1px solid transparent'
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)'
                }}
              >
                <div className='flex items-center justify-between w-full'>
                  <div className='flex items-center space-x-3 flex-1 min-w-0'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center space-x-2 mb-1'>
                        <Text
                          strong
                          className='truncate group-hover:text-blue-700 transition-colors duration-200'
                          style={{
                            fontSize: '15px',
                            color: portalColors.neutral[900]
                          }}
                        >
                          {display.title}
                        </Text>
                      </div>
                      <div className='flex items-center space-x-2'>
                        <Text
                          className='truncate'
                          style={{
                            fontSize: '13px',
                            color: portalColors.neutral[600]
                          }}
                        >
                          {display.company}
                        </Text>
                        {display.pipelineStage && (
                          <>
                            <span
                              style={{
                                color: portalColors.neutral[300],
                                fontSize: '12px'
                              }}
                            >
                              •
                            </span>
                            <Text
                              className='truncate'
                              style={{
                                fontSize: '13px',
                                color: portalColors.primary[600],
                                fontWeight: '500'
                              }}
                            >
                              {display.pipelineStage}
                            </Text>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {onExpandClick && (
                    <div
                      className='flex items-center justify-center w-8 h-8 ml-3 rounded-full opacity-0 group-hover:opacity-100 hover:bg-white hover:shadow-md transition-all duration-200 cursor-pointer'
                      onClick={(e) => handleExpandClick(item, e)}
                      title='Open full form'
                      style={{
                        border: `1px solid ${portalColors.primary[200]}`
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faExpandArrowsAlt}
                        style={{
                          color: portalColors.primary[500],
                          fontSize: '12px'
                        }}
                      />
                    </div>
                  )}
                </div>
              </List.Item>
            )
          }}
        />
      </div>
    )
  }

  return (
    <Popover
      content={renderContent()}
      title={null}
      trigger={[]}
      open={isFocused && renderContent() !== null}
      onOpenChange={(newOpen) => {
        // When popover is manually closed, also unfocus
        if (!newOpen) {
          setIsFocused(false)
        }
        // Open state is now controlled by focus, not by this callback
      }}
      placement='bottomLeft'
      classNames={{
        root: 'search-popover'
      }}
      styles={{
        root: {
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          background: 'white',
          overflow: 'hidden',
          width: '400px'
        }
      }}
    >
      <div className={`flex-1 ${className}`} style={{ minWidth: '200px', maxWidth: '400px' }}>
        <Input
          placeholder={placeholder}
          size='small'
          value={searchValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsFocused(true)
          }}
          onBlur={(e) => {
            // Delay to allow clicking on popover items
            setTimeout(() => {
              setIsFocused(false)
            }, 150)
          }}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: portalColors.neutral[400] }} />}
          allowClear
          className='bg-white'
          style={{
            borderRadius: '6px',
            width: '100%',
            height: '32px'
          }}
        />
      </div>
    </Popover>
  )
}
